import BaseBuilderController from "./base_builder_controller"

/**
 * Metric Builder Controller
 *
 * Extends BaseBuilderController with metric-specific functionality:
 * - Data source selection
 * - Metric title and description management
 * - Dashboard layout options (cols, rows)
 *
 * Inherits from BaseBuilderController:
 * - Column management (add, remove, reorder via drag-drop)
 * - Column options (sort, aggregate)
 * - Filter management
 * - Grouping management
 * - Visualization type switching
 * - Chart and stat configuration
 * - Preview loading states
 * - Config sync and save operations
 * - Scope management
 */
export default class extends BaseBuilderController {
  static targets = [
    ...BaseBuilderController.targets,
    "reportName",
    "nameField",
    "dataSourceField",
    // Scope display targets
    "scopeBadge",
    // Metric-specific targets
    "metricTitle",
    "metricDescription",
    "colsField",
    "rowsField",
    "descriptionField"
  ]

  static values = {
    ...BaseBuilderController.values,
    metricId: Number,
    dashboardId: Number
  }

  // ==========================================
  // Abstract method implementations
  // ==========================================

  get entityId() {
    return this.metricIdValue
  }

  get filtersFrameUrl() {
    return `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/filters`
  }

  get groupHistoryMembersUrl() {
    return `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/group_history_members`
  }

  get groupingsFrameUrl() {
    return `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/groupings`
  }

  get previewFrameId() {
    return 'metric_preview'
  }

  // ==========================================
  // Data Source Selection
  // ==========================================

  changeDataSource(event) {
    // Get data source ID from button's data attribute
    const dataSourceId = event.currentTarget.dataset.dataSourceId

    // Update hidden field
    if (this.hasDataSourceFieldTarget) {
      this.dataSourceFieldTarget.value = dataSourceId
    }

    // Construct the builder URL from controller values
    const builderUrl = `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/builder`
    const url = new URL(builderUrl, window.location.origin)
    url.searchParams.set('data_source_id', dataSourceId)

    // Include current visualization config in URL so controller can preserve it
    if (this.visualization) {
      url.searchParams.set('preserve_viz', JSON.stringify(this.visualization))
    }

    // Reload just the modal frame instead of full page navigation
    const frame = document.querySelector('turbo-frame#dynamic_modal_frame')
    if (frame) {
      frame.src = url.toString()
    } else {
      // Fallback to full page navigation if frame not found
      window.location.href = url.toString()
    }
  }

  // ==========================================
  // Metric Name/Title Management
  // ==========================================

  updateName(event) {
    const value = event.target.value
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = value
    }
    // Also update the sidebar title input if it exists
    if (this.hasMetricTitleTarget && event.target !== this.metricTitleTarget) {
      this.metricTitleTarget.value = value
    }
    this.markDirty()
  }

  updateTitle(event) {
    const value = event.target.value
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = value
    }
    // Also update the header title input if it exists
    if (this.hasReportNameTarget && event.target !== this.reportNameTarget) {
      this.reportNameTarget.value = value
    }
    // Keep the stat preview's card header in sync as the user types.
    const previewTitle = this.element.querySelector("[data-builder-preview-title]")
    if (previewTitle) previewTitle.textContent = value
    this.markDirty()
  }

  updateDescription(event) {
    if (this.hasDescriptionFieldTarget) {
      this.descriptionFieldTarget.value = event.target.value
    }
    this.markDirty()
  }

  // ==========================================
  // Metric Layout Options
  // ==========================================

  updateLayout() {
    // Sync description from sidebar to hidden field
    if (this.hasDescriptionFieldTarget && this.hasMetricDescriptionTarget) {
      this.descriptionFieldTarget.value = this.metricDescriptionTarget.value
    }
  }

  // ==========================================
  // Metric-specific Sync
  // ==========================================

  syncEntitySpecificConfigs() {
    if (this.hasNameFieldTarget && this.hasReportNameTarget) {
      this.nameFieldTarget.value = this.reportNameTarget.value
    }

    // Sync metric layout
    this.updateLayout()
  }

  // ==========================================
  // Field Picker (override for metric-specific behavior)
  // ==========================================

  closeFieldPicker() {
    if (this.hasFieldPickerModalTarget) {
      this.fieldPickerModalTarget.close()
    }
    if (this.hasFieldSearchTarget) {
      this.fieldSearchTarget.value = ''
      this.filterFields({ target: { value: '' } })
    }
    // Preview is already updated when fields are added, no need to refresh again
  }

  // ==========================================
  // Preview (override for metric-specific URL)
  // ==========================================

  refreshPreview() {
    const frame = document.getElementById('metric_preview')
    if (frame && this.hasPreviewUrlValue && this.previewUrlValue) {
      frame.src = this.previewUrlValue
    } else if (frame && this.hasMetricIdValue && this.metricIdValue && this.hasDashboardIdValue) {
      frame.src = `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/preview`
    }
  }

  /**
   * Override to construct metric-specific preview URL with draft config
   */
  refreshPreviewWithDraft() {
    if (!this.hasMetricIdValue || !this.metricIdValue) return

    this.markDirty()
    this.showPreviewLoading()

    const frame = document.getElementById(this.previewFrameId)
    if (frame) {
      let baseUrl
      if (this.hasPreviewUrlValue && this.previewUrlValue) {
        baseUrl = this.previewUrlValue
      } else if (this.hasDashboardIdValue) {
        baseUrl = `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/preview`
      } else {
        return
      }

      const url = new URL(baseUrl, window.location.origin)
      url.searchParams.set('page', this.currentPage)
      url.searchParams.set('per_page', this.currentPerPage)

      // Add draft config for sandbox preview
      url.searchParams.set('draft_config', JSON.stringify(this.buildDraftConfig()))

      // Add ephemeral sort params
      if (this.ephemeralSort) {
        url.searchParams.set('sort_field', this.ephemeralSort.field_key)
        url.searchParams.set('sort_direction', this.ephemeralSort.direction)
      }

      // Add ephemeral filters (as JSON)
      if (this.ephemeralFilters.length > 0) {
        url.searchParams.set('ephemeral_filters', JSON.stringify(this.ephemeralFilters))
      }

      frame.src = url.toString()
    }
  }

  /**
   * Override to construct metric-specific preview URL for pagination
   */
  refreshPreviewWithParams() {
    if (!this.hasMetricIdValue || !this.metricIdValue) return

    this.showPreviewLoading()

    const frame = document.getElementById(this.previewFrameId)
    if (frame) {
      let baseUrl
      if (this.hasPreviewUrlValue && this.previewUrlValue) {
        baseUrl = this.previewUrlValue
      } else if (this.hasDashboardIdValue) {
        baseUrl = `/dashboards/${this.dashboardIdValue}/metrics/${this.metricIdValue}/preview`
      } else {
        return
      }

      const url = new URL(baseUrl, window.location.origin)
      url.searchParams.set('page', this.currentPage)
      url.searchParams.set('per_page', this.currentPerPage)

      // Add draft config to preserve unsaved visualization type
      url.searchParams.set('draft_config', JSON.stringify(this.buildDraftConfig()))

      // Add ephemeral sort params
      if (this.ephemeralSort) {
        url.searchParams.set('sort_field', this.ephemeralSort.field_key)
        url.searchParams.set('sort_direction', this.ephemeralSort.direction)
      }

      // Add ephemeral filters (as JSON)
      if (this.ephemeralFilters.length > 0) {
        url.searchParams.set('ephemeral_filters', JSON.stringify(this.ephemeralFilters))
      }

      frame.src = url.toString()
    }
  }

  // ==========================================
  // Save And Refresh (override for null check)
  // ==========================================

  saveAndRefresh() {
    if (!this.hasMetricIdValue || !this.metricIdValue) return
    super.saveAndRefresh()
  }
}
